import {
  Add,
  Delete,
  ContentCopy,
  CheckCircle,
  Cancel,
  Schedule,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import QuoteCreationDialog from "./dialogs/QuoteCreationDialog";

interface Quote {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pricingData: any;
  status: string;
  notes?: string;
  paidDate?: string;
  expirationDate?: string;
  created_date: string;
  updated_date: string;
}

export default function WhitelabelQuotes({
  whitelabelId,
  currentUser,
  getToken,
}: {
  whitelabelId: string;
  currentUser: User;
  getToken: () => Promise<string>;
}) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error"
  >("success");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<Quote | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    loadQuotes();
  }, [whitelabelId]);

  const loadQuotes = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/quotes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setQuotes(response.data.quotes || []);
    } catch (err: any) {
      console.error("Error loading quotes:", err);
      setError(err.response?.data?.error || "Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleDelete = async () => {
    if (!quoteToDelete) return;

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/quotes/${quoteToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSnackbar("Quote deleted successfully", "success");
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
      loadQuotes();
    } catch (err: any) {
      console.error("Error deleting quote:", err);
      showSnackbar(
        err.response?.data?.error || "Failed to delete quote",
        "error"
      );
    }
  };

  const copyQuoteUrl = (quoteId: string) => {
    const url = `${window.location.origin}/quote/${quoteId}`;
    navigator.clipboard.writeText(url);
    showSnackbar("Quote URL copied to clipboard", "success");
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case "paid":
        return (
          <Chip
            icon={<CheckCircle />}
            label="Paid"
            color="success"
            size="small"
          />
        );
      case "pending":
        return (
          <Chip
            icon={<Schedule />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
      case "expired":
        return (
          <Chip icon={<Cancel />} label="Expired" color="error" size="small" />
        );
      case "cancelled":
        return (
          <Chip
            icon={<Cancel />}
            label="Cancelled"
            color="default"
            size="small"
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading quotes...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">Custom Quotes</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Quote
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Beta Feature:</strong> The quote service is currently in beta. Please test thoroughly before using with customers.
      </Alert>

      <Alert severity="info" sx={{ mb: 3 }}>
        Create custom quotes for customers with personalized pricing. Share the
        quote URL and customers can pay directly, which will automatically
        create their user account.
      </Alert>

      {quotes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" gutterBottom>
            No quotes created yet
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mt: 2 }}
          >
            Create Your First Quote
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((quote) => (
                <TableRow key={quote.id}>
                  <TableCell>
                    {quote.firstName} {quote.lastName}
                  </TableCell>
                  <TableCell>{quote.email}</TableCell>
                  <TableCell>
                    {quote.pricingData?.tierName || quote.pricingData?.plan}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(
                      quote.pricingData?.totalAmount ||
                        quote.pricingData?.monthlyPrice ||
                        0
                    )}
                    {quote.pricingData?.billingPeriod === "annual"
                      ? "/year"
                      : "/month"}
                  </TableCell>
                  <TableCell>{getStatusChip(quote.status)}</TableCell>
                  <TableCell>{formatDate(quote.created_date)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Copy Quote URL">
                      <IconButton
                        size="small"
                        onClick={() => copyQuoteUrl(quote.id)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {quote.status !== "paid" && (
                      <Tooltip title="Delete Quote">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setQuoteToDelete(quote);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Quote</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the quote for{" "}
            {quoteToDelete?.firstName} {quoteToDelete?.lastName}?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Quote Dialog */}
      <QuoteCreationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        getToken={getToken}
        whitelabelId={whitelabelId}
        onQuoteCreated={() => {
          showSnackbar("Quote created successfully", "success");
          loadQuotes();
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
